import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Upload, Download, Users, FileText, CheckCircle, 
  XCircle, AlertCircle, FileSpreadsheet, Layers, Lock
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const BulkImport = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('students');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [hasAccess, setHasAccess] = useState(true);
  
  // File refs
  const studentFileRef = useRef(null);
  const resultFileRef = useRef(null);
  const sectionFileRef = useRef(null);
  
  // Upload in progress ref (to prevent double submissions)
  const uploadInProgressRef = useRef(false);
  
  // Data for dropdowns
  const [assessments, setAssessments] = useState([]);
  const [selectedAssessment, setSelectedAssessment] = useState('');
  
  // Update existing option
  const [updateExisting, setUpdateExisting] = useState(false);
  
  // Check subscription access
  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await api.get('/school-admin/subscription');
        if (!res.data.bulk_import) {
          setHasAccess(false);
          toast.error('Bulk Import requires Professional or Premium subscription');
        }
      } catch (err) {
        // Default to no access if check fails
        setHasAccess(false);
      }
    };
    checkAccess();
  }, []);
  
  // Fetch assessments
  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        const response = await api.get('/igcse/assessments');
        setAssessments(response.data);
      } catch (error) {
        console.error('Error fetching assessments:', error);
      }
    };
    fetchAssessments();
  }, []);

  // Download template
  const downloadTemplate = async (type) => {
    try {
      const response = await api.get(`/bulk/${type}/template`);
      const template = response.data.template;
      
      // Create and download file
      const blob = new Blob([template], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_template.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('Error downloading template');
    }
  };

  // Upload students
  const handleStudentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Prevent double submission using ref
    if (uploadInProgressRef.current) {
      console.log('Upload already in progress, ignoring duplicate request');
      e.target.value = '';
      return;
    }
    
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setResults({ 
        type: 'students', 
        data: { 
          success: 0, 
          failed: 1, 
          errors: ['Please upload a CSV file (.csv extension)'] 
        } 
      });
      e.target.value = '';
      return;
    }
    
    // Mark upload as in progress
    uploadInProgressRef.current = true;
    
    // Clear file input immediately to prevent re-triggering
    e.target.value = '';
    
    setLoading(true);
    setResults(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      // Add update_existing query parameter if checked
      const url = updateExisting ? '/bulk/students?update_existing=true' : '/bulk/students';
      const response = await api.post(url, formData);
      setResults({ type: 'students', data: response.data });
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Upload failed';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timed out. Please try with a smaller file.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please reduce the number of rows.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setResults({ 
        type: 'students', 
        data: { 
          success: 0, 
          failed: 1, 
          errors: [errorMessage] 
        } 
      });
    } finally {
      setLoading(false);
      uploadInProgressRef.current = false;
    }
  };

  // Upload results
  const handleResultUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setResults({ 
        type: 'results', 
        data: { 
          success: 0, 
          failed: 1, 
          errors: ['Please upload a CSV file (.csv extension)'] 
        } 
      });
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    let url = '/bulk/results';
    if (selectedAssessment) {
      url += `?assessment_id=${selectedAssessment}`;
    }
    
    try {
      const response = await api.post(url, formData);
      setResults({ type: 'results', data: response.data });
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Upload failed';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timed out. Please try with a smaller file.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please reduce the number of rows.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setResults({ 
        type: 'results', 
        data: { 
          success: 0, 
          failed: 1, 
          errors: [errorMessage] 
        } 
      });
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  // Upload section assignments
  const handleSectionUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setResults({ 
        type: 'sections', 
        data: { 
          success: 0, 
          failed: 1, 
          errors: ['Please upload a CSV file (.csv extension)'] 
        } 
      });
      return;
    }
    
    setLoading(true);
    setResults(null);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post('/bulk/assign-sections', formData);
      setResults({ type: 'sections', data: response.data });
    } catch (error) {
      console.error('Upload error:', error);
      let errorMessage = 'Upload failed';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage = 'Upload timed out. Please try with a smaller file.';
      } else if (error.response?.status === 413) {
        errorMessage = 'File too large. Please reduce the number of rows.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setResults({ 
        type: 'sections', 
        data: { 
          success: 0, 
          failed: 1, 
          errors: [errorMessage] 
        } 
      });
    } finally {
      setLoading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Bulk Import</h1>
              <p className="text-sm text-gray-500">Import students and marks from CSV files</p>
            </div>
          </div>
        </div>
      </div>

      {/* Access Denied Message */}
      {!hasAccess && (
        <div className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-xl p-8 shadow-sm border text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Upgrade Required</h2>
            <p className="text-gray-600 mb-6">
              Bulk Import is available with Professional and Premium subscriptions.
              Contact your administrator to upgrade.
            </p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {hasAccess && (
        <>
      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2">
            {[
              { id: 'students', label: 'Import Students', icon: Users },
              { id: 'results', label: 'Import Marks/Results', icon: FileText },
              { id: 'sections', label: 'Assign Sections', icon: Layers },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setResults(null); }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id 
                    ? 'bg-indigo-600 text-white' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Import Students Tab */}
        {activeTab === 'students' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-600" />
                Bulk Import Students
              </h2>
              
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-blue-800 mb-2">CSV Format Requirements:</h3>
                <div className="text-sm text-blue-700 space-y-1">
                  <p><strong>Required columns:</strong> name, email</p>
                  <p><strong>Optional columns:</strong> password, grade, section, board, subjects</p>
                  <p><strong>Note:</strong> If password is empty, default "Student123$" will be used</p>
                  <p><strong>Subjects:</strong> Comma-separated list (e.g., "Physics,Chemistry,Biology")</p>
                </div>
              </div>

              <div className="flex gap-4 mb-4">
                <button
                  onClick={() => downloadTemplate('students')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  data-testid="download-student-template"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
                
                <input
                  type="file"
                  ref={studentFileRef}
                  accept=".csv"
                  onChange={handleStudentUpload}
                  className="hidden"
                />
                
                <button
                  onClick={() => studentFileRef.current?.click()}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                  data-testid="upload-students-btn"
                >
                  <Upload className="w-4 h-4" />
                  {loading ? 'Uploading...' : 'Upload CSV'}
                </button>
              </div>

              {/* Update existing checkbox */}
              <div className="flex items-center gap-2 mb-6">
                <input
                  type="checkbox"
                  id="updateExisting"
                  checked={updateExisting}
                  onChange={(e) => setUpdateExisting(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                  data-testid="update-existing-checkbox"
                />
                <label htmlFor="updateExisting" className="text-sm text-gray-700">
                  <span className="font-medium">Update existing students</span>
                  <span className="text-gray-500 ml-1">(if email already exists, update their details instead of failing)</span>
                </label>
              </div>

              {/* Sample Data Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">name</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">email</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">password</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">grade</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">section</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">board</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">subjects</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-t">
                      <td className="px-4 py-2">John Smith</td>
                      <td className="px-4 py-2">john@school.edu</td>
                      <td className="px-4 py-2">Student123$</td>
                      <td className="px-4 py-2">Grade 10</td>
                      <td className="px-4 py-2">Section A</td>
                      <td className="px-4 py-2">IGCSE</td>
                      <td className="px-4 py-2">Physics,Chemistry</td>
                    </tr>
                    <tr className="border-t bg-gray-50">
                      <td className="px-4 py-2">Jane Doe</td>
                      <td className="px-4 py-2">jane@school.edu</td>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2">Grade 10</td>
                      <td className="px-4 py-2">Section B</td>
                      <td className="px-4 py-2">IGCSE</td>
                      <td className="px-4 py-2">Physics,Biology</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Import Results Tab */}
        {activeTab === 'results' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Bulk Import Marks/Results
              </h2>
              
              <div className="bg-green-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-green-800 mb-2">CSV Format Requirements:</h3>
                <div className="text-sm text-green-700 space-y-1">
                  <p><strong>Required columns:</strong> student_email, subject, marks_obtained</p>
                  <p><strong>Optional columns:</strong> max_marks (default: 100), section</p>
                  <p><strong>Subject:</strong> Use subject name or code (e.g., "Physics" or "PHY")</p>
                </div>
              </div>

              {/* Optional Assessment Selection */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link to Assessment (Optional)
                </label>
                <select
                  value={selectedAssessment}
                  onChange={(e) => setSelectedAssessment(e.target.value)}
                  className="w-full max-w-md px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">-- No specific assessment --</option>
                  {assessments.map(assessment => (
                    <option key={assessment.id} value={assessment.id}>
                      {assessment.name} ({assessment.assessment_type_code})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  If selected, all imported marks will be linked to this assessment
                </p>
              </div>

              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => downloadTemplate('results')}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                  data-testid="download-results-template"
                >
                  <Download className="w-4 h-4" />
                  Download Template
                </button>
                
                <input
                  type="file"
                  ref={resultFileRef}
                  accept=".csv"
                  onChange={handleResultUpload}
                  className="hidden"
                />
                
                <button
                  onClick={() => resultFileRef.current?.click()}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                  data-testid="upload-results-btn"
                >
                  <Upload className="w-4 h-4" />
                  {loading ? 'Uploading...' : 'Upload CSV'}
                </button>
              </div>

              {/* Sample Data Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">student_email</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">subject</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">marks_obtained</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">max_marks</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">section</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-t">
                      <td className="px-4 py-2">john@school.edu</td>
                      <td className="px-4 py-2">Physics</td>
                      <td className="px-4 py-2">85</td>
                      <td className="px-4 py-2">100</td>
                      <td className="px-4 py-2">Section A</td>
                    </tr>
                    <tr className="border-t bg-gray-50">
                      <td className="px-4 py-2">john@school.edu</td>
                      <td className="px-4 py-2">Chemistry</td>
                      <td className="px-4 py-2">78</td>
                      <td className="px-4 py-2">100</td>
                      <td className="px-4 py-2">Section A</td>
                    </tr>
                    <tr className="border-t">
                      <td className="px-4 py-2">jane@school.edu</td>
                      <td className="px-4 py-2">PHY</td>
                      <td className="px-4 py-2">92</td>
                      <td className="px-4 py-2">100</td>
                      <td className="px-4 py-2">Section B</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Assign Sections Tab */}
        {activeTab === 'sections' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5 text-indigo-600" />
                Bulk Assign Students to Sections
              </h2>
              
              <div className="bg-purple-50 rounded-lg p-4 mb-6">
                <h3 className="font-medium text-purple-800 mb-2">CSV Format Requirements:</h3>
                <div className="text-sm text-purple-700 space-y-1">
                  <p><strong>Required columns:</strong> student_email, section</p>
                  <p><strong>Note:</strong> Section must already exist in the system</p>
                </div>
              </div>

              <div className="flex gap-4 mb-6">
                <input
                  type="file"
                  ref={sectionFileRef}
                  accept=".csv"
                  onChange={handleSectionUpload}
                  className="hidden"
                />
                
                <button
                  onClick={() => sectionFileRef.current?.click()}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  data-testid="upload-sections-btn"
                >
                  <Upload className="w-4 h-4" />
                  {loading ? 'Uploading...' : 'Upload CSV'}
                </button>
              </div>

              {/* Sample Data Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">student_email</th>
                      <th className="px-4 py-2 text-left font-medium text-gray-700">section</th>
                    </tr>
                  </thead>
                  <tbody className="text-gray-600">
                    <tr className="border-t">
                      <td className="px-4 py-2">john@school.edu</td>
                      <td className="px-4 py-2">Section A</td>
                    </tr>
                    <tr className="border-t bg-gray-50">
                      <td className="px-4 py-2">jane@school.edu</td>
                      <td className="px-4 py-2">Section B</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Results Display */}
        {results && (
          <div className="mt-6 bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Import Results</h3>
            
            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-700">{results.data.success}</p>
                <p className="text-sm text-green-600">Successful</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <XCircle className="w-8 h-8 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-700">{results.data.failed}</p>
                <p className="text-sm text-red-600">Failed</p>
              </div>
            </div>

            {/* Errors */}
            {results.data.errors && results.data.errors.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Errors ({results.data.errors.length})
                </h4>
                <div className="bg-red-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-1">
                    {results.data.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Created Students */}
            {results.type === 'students' && results.data.created_students && results.data.created_students.length > 0 && (
              <div>
                <h4 className="font-medium text-green-700 mb-2">Created Students</h4>
                <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Name</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Grade</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Section</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      {results.data.created_students.map((student, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2">{student.name}</td>
                          <td className="px-4 py-2">{student.email}</td>
                          <td className="px-4 py-2">{student.grade || '-'}</td>
                          <td className="px-4 py-2">{student.section || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recorded Results */}
            {results.type === 'results' && results.data.recorded_results && results.data.recorded_results.length > 0 && (
              <div>
                <h4 className="font-medium text-green-700 mb-2">Recorded Results</h4>
                <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Student</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Subject</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Marks</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">%</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Grade</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Action</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600">
                      {results.data.recorded_results.map((result, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2">{result.student}</td>
                          <td className="px-4 py-2">{result.subject}</td>
                          <td className="px-4 py-2">{result.marks}</td>
                          <td className="px-4 py-2">{result.percentage}</td>
                          <td className="px-4 py-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                              result.grade === 'A*' ? 'bg-emerald-100 text-emerald-700' :
                              result.grade === 'A' ? 'bg-green-100 text-green-700' :
                              result.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                              result.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {result.grade}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500">{result.action}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

export default BulkImport;
